import { colors as chameleon, lightTheme, tokens } from '@getgo/chameleon-core';
import shadows from './shadows';
// Everything Material-UI related that we want to override goes here
// https://material-ui.com/customization/components/#global-theme-override
const overrides = {
    MuiButton: {
        root: {
            minWidth: 36,
            textTransform: 'unset',
            lineHeight: 'unset',
            '-webkit-app-region': 'no-drag',
        },
    },
    MuiIconButton: {
        root: {
            borderRadius: 4,
        },
    },
    // @ts-ignore
    MuiSkeleton: {
        root: {
            borderRadius: 4,
        },
    },
    MuiDrawer: {
        paper: {
            zIndex: 1195,
        },
    },
    MuiLink: {
        root: {
            fontWeight: 500,
        },
    },
    MuiMobileStepper: {
        root: {
            background: 'transparent',
        },
    },
    MuiBreadcrumbs: {
        ol: {
            flexWrap: 'nowrap',
        },
    },
    MuiAlert: {
        message: {
            alignSelf: 'center',
        },
    },
    MuiInputAdornment: {
        positionStart: {
            marginRight: 4,
            marginLeft: -4,
        },
        positionEnd: {
            marginRight: -4,
            marginLeft: 4,
        },
    },
    MuiPopover: {
        paper: {
            overflowX: 'unset',
            overflowY: 'unset',
        },
    },
    MuiDivider: {
        root: {
            backgroundColor: lightTheme['field-01'],
        },
    },
    MuiListItemIcon: {
        root: {
            backgroundColor: 'transparent',
            minWidth: 32,
        },
    },
    MuiMenu: {
        paper: {
            boxShadow: shadows[3],
            overflowY: 'auto',
            overflowX: 'hidden',
            maxHeight: '70vh',
        },
    },
    MuiMenuItem: {
        root: {
            fontSize: 14,
        },
    },
    // @ts-ignore
    MuiAutocomplete: {
        endAdornment: {
            top: 'calc(50% - 13px)',
            right: 11,
        },
        option: {
            fontSize: 14,
        },
        tag: {
            margin: 4,
        },
    },
    MuiSnackbarContent: {
        root: {
            backgroundColor: chameleon.neutrals.n600,
            maxWidth: 480,
            boxShadow: shadows[3],
        },
    },
    MuiSnackbar: {
        anchorOriginBottomLeft: {
            alignItems: 'flex-end',
        },
    },
    MuiSvgIcon: {
        fontSizeSmall: {
            fontSize: '1rem!important',
        },
    },
    MuiBackdrop: {
        root: {
            opacity: 1,
            backgroundColor: tokens.overlay01,
        },
    },
    MuiDialog: {
        paperWidthXs: {
            maxWidth: 400,
        },
        paperWidthSm: {
            maxWidth: 480,
        },
        paperWidthMd: {
            maxWidth: 640,
        },
    },
    MuiChip: {
        icon: {
            marginLeft: 10,
            color: 'rgba(37, 40, 45, 0.64)',
        },
        clickable: {
            '&:hover': {
                backgroundColor: 'rgba(154, 168, 187, 0.32)',
            },
            '&:active': {
                backgroundColor: 'rgba(154, 168, 187, 0.48)',
            },
        },
        iconSmall: {
            marginLeft: 6,
        },
        root: {
            boxShadow: 'none',
            border: `1px solid transparent`,
            backgroundColor: 'rgba(154, 168, 187, 0.16)',
            '&:hover': {
                '&$disabled': {
                    pointerEvents: 'auto',
                },
            },
            // @ts-ignore
            [`& .${window.CLASSNAME_PREFIX}-MuiChip-avatar`]: {
                width: 32,
                height: 32,
                marginLeft: 0,
                marginRight: -4,
            },
            '&$disabled': {
                color: 'rgba(37, 40, 45, 0.36)',
                backgroundColor: 'rgba(154, 168, 187, 0.12);',
                opacity: 1,
                // @ts-ignore
                [`& .${window.CLASSNAME_PREFIX}-MuiChip-icon`]: {
                    color: 'rgba(37, 40, 45, 0.24)',
                },
                // @ts-ignore
                [`& .${window.CLASSNAME_PREFIX}-MuiChip-deleteIcon`]: {
                    color: 'rgba(37, 40, 45, 0.24)',
                },
                // @ts-ignore
                [`& .${window.CLASSNAME_PREFIX}-MuiChip-avatar`]: {
                    opacity: 0.7,
                },
            },
        },
        deleteIcon: {
            color: 'rgba(37, 40, 45, 0.64)',
            marginRight: 10,
            width: 16,
            height: 16,
            '&:hover': {
                color: 'rgba(37, 40, 45, 0.64)',
            },
        },
        deleteIconSmall: {
            marginRight: 6,
        },
        clickableColorPrimary: {
            '&:hover': {
                backgroundColor: chameleon.blue.b600,
            },
            '&:active': {
                backgroundColor: '#003989',
            },
        },
        sizeSmall: {
            height: 24,
            // @ts-ignore
            [`& .${window.CLASSNAME_PREFIX}-MuiChip-avatar`]: {
                width: 24,
                height: 24,
                marginLeft: 0,
                marginRight: -2,
            },
        },
    },
    MuiSwitch: {
        colorSecondary: {
            '&:hover': {
                backgroundColor: 'transparent',
            },
            '&$checked': {
                color: tokens.neutral000,
                '& + $track': {
                    backgroundColor: tokens.information01,
                    opacity: 1,
                },
                '&$disabled': {
                    '& + $track': {
                        backgroundColor: tokens.neutral200,
                    },
                },
                '&:hover': {
                    backgroundColor: 'transparent',
                },
            },
            '&$disabled': {
                color: tokens.neutral000,
                '& + $track': {
                    backgroundColor: tokens.neutral200,
                    opacity: 1,
                },
            },
        },
        switchBase: {
            color: tokens.neutral000,
            padding: 2,
            '&::before': {
                transition: 'inherit',
                left: 0,
            },
            '&$checked': {
                transform: 'translateX(18px)',
                '&::before': {
                    transition: 'inherit',
                    transform: 'translateX(-18px)',
                },
            },
        },
        root: {
            border: `1px solid transparent`,
            padding: 0,
            height: 22,
            width: 40,
            overflow: 'unset',
        },
        thumb: {
            width: 16,
            height: 16,
            boxShadow: '0px 3px 1px -2px rgba(0, 0, 0, 0.12), 0px 2px 2px 0px rgba(0, 0, 0, 0.08), 0px 1px 5px 0px rgba(0, 0, 0, 0.04)',
        },
        track: {
            borderRadius: 10,
            backgroundColor: tokens.ui03,
            opacity: 1,
        },
    },
    MuiButtonBase: {
        root: {
            '&:disabled': {
                pointerEvents: 'auto',
            },
            '&$disabled': {
                pointerEvents: 'auto',
            },
        },
    },
    MuiInputBase: {
        root: {
            '&:hover': {
                '&$disabled': {
                    pointerEvents: 'auto',
                },
            },
            '&$disabled': {
                color: lightTheme['text-03'],
                borderColor: lightTheme['disabled-04'],
                backgroundColor: lightTheme['disabled-02'],
            },
        },
        input: {
            '&$disabled': {
                pointerEvents: 'none',
            },
        },
    },
    MuiTab: {
        root: {
            minWidth: '80px !important',
            textTransform: 'unset',
        },
    },
    MuiTableCell: {
        root: {
            height: 24,
            borderBottom: 'none',
        },
        head: {
            fontSize: 12,
            fontWeight: 600,
            paddingTop: 0,
            paddingBottom: 0,
            cursor: 'auto',
            color: chameleon.neutrals.n400,
            borderBottom: `1px solid ${lightTheme['field-01']}`,
        },
        body: {
            fontSize: 14,
            borderBottom: `1px solid ${lightTheme['field-01']}`,
        },
    },
    MuiTabs: {
        indicator: {
            height: 4,
            borderTopLeftRadius: 4,
            borderTopRightRadius: 4,
            backgroundColor: tokens.tabsIndicatorBackground,
        },
    },
    MuiExpansionPanelSummary: {
        root: {
            padding: '0px 20px',
        },
        content: {
            margin: '20px 0px',
        },
    },
    MuiAccordionSummary: {
        root: {
            padding: '0px 20px',
        },
        content: {
            margin: '20px 0px',
        },
    },
    MuiBadge: {
        root: {
            width: '100%',
        },
    },
    MuiAvatar: {
        // Need to specify this for AvatarGroup
        root: {
            height: 36,
            width: 36,
            fontSize: '0.875rem',
            fontWeight: 500,
        },
    },
};
export default overrides;
